import { useEffect, useState } from "react";
import { useSyntecContext } from "api/settings/syntec/SyntecProvider";
import { FormattedMessage } from "react-intl";
import { HandleModaleBO } from "components/bo/HandleModaleBO";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { TableManager } from "components/bo/admin-table/TableManager";
import { ACTIONS, ENTITIES, LANG, VAR_TYPES } from "constants/constants";
import { navigateView, navigateEdit, navigateToParent } from "utils/boHelper";
import Button from "core/buttons/button/Button";
import PropTypes from "prop-types";
import { useContractualStatusContext } from "api/settings/contractual-status/ContractualStatusProvider";
import { useUserContext } from "api/user/UserProvider";

export default function AdminSyntecPosCoefStaff({ action }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();
  const { lang } = useUserContext();
  const {
    syntecCoefStaffById,
    syntecCoefStaffCreate,
    syntecCoefStaffDeleteById,
    syntecCoefStaffListGetter,
    syntecCoefStaffUpdateById,
    syntecList,
    syntecListGetter,
    syntecCoefStaffByIdGetter,
    syntecCoefStaffList,
  } = useSyntecContext();
  const { contractualStatusList, contractualStatusListGetter } =
    useContractualStatusContext();
  const [deleteData, setDeleteData] = useState(null);
  const [inputs, setInputs] = useState([]);
  const [contractualStatusSelected, setContractualStatusSelected] = useState(
    {}
  );

  useEffect(() => {
    syntecListGetter();
    syntecCoefStaffListGetter();
    contractualStatusListGetter();
  }, []);

  useEffect(() => {
    if (id) {
      syntecCoefStaffByIdGetter({
        id,
      });
    }
  }, [id]);

  const syntecCoefStaffListFlatted = syntecCoefStaffList.flatMap((e) =>
    e.coef_position_syntec.map((elm) => ({ ...elm }))
  );

  const listToDisplay = syntecCoefStaffListFlatted.filter((e) => {
    return e.contractual_status_id === contractualStatusSelected.id;
  });

  const handleDelete = async (syntecPosCoefId) => {
    return setDeleteData(
      syntecCoefStaffListFlatted.find((e) => e.id === syntecPosCoefId)
    );
  };

  const validateDelete = async (syntecPosCoefId) => {
    try {
      await syntecCoefStaffDeleteById({
        id: syntecPosCoefId,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreate = (payload) => {
    try {
      syntecCoefStaffCreate({
        data: {
          ...payload,
        },
      });
      navigateToParent(navigate, location);
    } catch (e) {
      console.error(e);
    }
  };

  const handleEdit = (payload) => {
    try {
      syntecCoefStaffUpdateById({
        id: payload.id,
        data: payload,
      });
      navigateToParent(navigate, location);
    } catch (e) {
      console.error(e);
    }
  };

  const formatContractualStatus = ({ contractualStatusId }) => {
    const contractStatus = contractualStatusList?.find(
      (e) => e.id === contractualStatusId
    );
    return lang === LANG.FR ? contractStatus?.fr : contractStatus?.en;
  };

  const formatList = ({ list }) => {
    const formattedList = list.map((elm) => ({
      ...elm,
      syntec: elm.syntec_id
        ? syntecList.find((e) => e.id === elm.syntec_id)?.name
        : "",
      contractual_status_id: formatContractualStatus({
        contractualStatusId: elm.contractual_status_id,
      }),
    }));
    return (
      formattedList?.length > 0 &&
      formattedList.filter((el) => {
        return el?.contractual_status_id === contractualStatusSelected.label;
      })
    );
  };

  const formatData = ({ data }) => {
    return data?.syntec_id
      ? {
          ...data,
          syntec_id: syntecList.find((e) => e.id === data.syntec_id)?.name,
          contractual_status_id: formatContractualStatus({
            contractualStatusId: data.contractual_status_id,
          }),
        }
      : "";
  };

  useEffect(() => {
    if (contractualStatusList?.length > 0) {
      setContractualStatusSelected({
        id: contractualStatusList[0].id,
        label:
          lang === LANG.FR
            ? contractualStatusList[0].fr
            : contractualStatusList[0].en,
      });
    }
  }, [contractualStatusList, lang]);

  useEffect(() => {
    if (syntecCoefStaffListFlatted?.length > 0) {
      const syntecChoices = [{ name: "", value: null }].concat(
        syntecList.map((e) => ({
          name: e.name,
          value: e.id.toString(),
        }))
      );
      const contractualStatusChoices = [{ name: "", value: null }].concat(
        contractualStatusList?.map((el) => {
          return {
            name: lang === LANG.FR ? el.fr : el.en,
            value: el.id,
          };
        })
      );
      setInputs([
        {
          col: "contractual_status_id",
          mandatory: true,
          editable: true,
          isMissingValue: null,
          type: VAR_TYPES.DROPDOWN,
          choices: contractualStatusChoices,
          toBeParsed: true,
        },
        {
          col: "position",
          mandatory: true,
          type: VAR_TYPES.STRING,
          step: "0.1",
          isMissingValue: null,
          editable: true,
        },
        {
          col: "coefficient",
          mandatory: true,
          type: VAR_TYPES.NUMBER,
          step: "1",
          isMissingValue: null,
          editable: true,
        },
        {
          col: "gross_conventional_minimum",
          mandatory: true,
          type: VAR_TYPES.NUMBER,
          step: "0.01",
          isMissingValue: null,
          editable: true,
        },
        {
          col: "syntec_id",
          mandatory: true,
          editable: true,
          isMissingValue: null,
          type: VAR_TYPES.DROPDOWN,
          choices: syntecChoices,
        },
        {
          col: "comment",
          mandatory: false,
          editable: true,
          isMissingValue: null,
          type: VAR_TYPES.STRING,
        },
      ]);
    }
  }, [syntecList, syntecCoefStaffList, lang]);

  return (
    <div>
      {action === ACTIONS.LIST && (
        <div className="flex">
          <Button
            className="btn-hover-color"
            size="large"
            color="blue"
            onClick={() => navigateView("new", navigate, location)}
          >
            <FormattedMessage
              id={"admin_create_new"}
              defaultMessage={"Nouveau"}
            />
          </Button>
        </div>
      )}
      <br />
      <TableManager
        lang={lang}
        action={action}
        entity={ENTITIES.SYNTEC_POS_COEF_STAFF}
        entityPerId={syntecCoefStaffById}
        formattedEntity={formatData({
          data: syntecCoefStaffById,
        })}
        list={formatList({ list: listToDisplay })}
        inputs={inputs}
        navigateEdit={(propsId) => navigateEdit(propsId, navigate, location)}
        navigateView={(propsId) => navigateView(propsId, navigate, location)}
        handleCreate={handleCreate}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
        contractualStatusList={contractualStatusList}
        contractualStatusSelected={contractualStatusSelected}
        setContractualStatusSelected={setContractualStatusSelected}
      />
      <div id="modalDelete">
        <HandleModaleBO
          setDeleteData={setDeleteData}
          validateDelete={validateDelete}
          deleteData={formatData({ data: deleteData })}
          entity={ENTITIES.SYNTEC_POS_COEF_STAFF}
        />
      </div>
    </div>
  );
}

AdminSyntecPosCoefStaff.propTypes = {
  action: PropTypes.string,
};
